import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";
import { API_URL } from "../../confi";
const BlogContext = createContext();

export const BlogProvider = ({ children }) => {
  const { token, isAuthenticated, logout } = useAuth();
  const [fetchblogs, setFetchBlogs] = useState([]);
  // const API_BASE = `${API_URL}/api`;

  const getBlogs = async () => {
    if (!isAuthenticated || !token) {
      console.log("No token, skipping blog fetch");
      return;
    }
    try {
      // console.log("Fetching blogs with token:", token.substring(0, 20) + "...");
      const res = await fetch(`${API_URL}/api/blogs`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const data = await res.json();
      // console.log("Fetch Blogs Response:", JSON.stringify(data, null, 2));

      if (res.ok) {
        setFetchBlogs(data.blogs?.data || data.blogs || []);
      } else {
        if (res.status === 401) {
          console.error("401 Unauthorized - Token:", token.substring(0, 20) + "...");
          toast.error("Session expired. Please log in again.");
          logout();
        } else {
          toast.error(data.message || "Failed to fetch blogs");
        }
      }
    } catch (err) {
      console.error("Fetch blogs error:", err);
      toast.error("Failed to fetch blogs");
    }
  };
    // =======================================================================================================// update Blog


  const updateBlog = async (blogId, updatedData) => {
  if (!isAuthenticated || !token) {
    console.error("No token available for update. isAuthenticated:", isAuthenticated, "Token:", token, "localStorage.apiToken:", localStorage.getItem("apiToken"));
    toast.error("Please log in to update blogs.");
    return;
  }
  try {
    const formData = new FormData();
    formData.append("id", blogId);
    formData.append("title", updatedData.title);
    formData.append("author_name", updatedData.author_name);
    formData.append("content", updatedData.content);
    if (updatedData.image) formData.append("image", updatedData.image);

    // Append SEO fields
    formData.append("title_tag", updatedData.title_tag || updatedData.title || "");
    formData.append("meta_title", updatedData.title_tag || updatedData.title || "");
    formData.append("meta_description", updatedData.meta_description || "");
    formData.append("keywords", updatedData.keywords || "");
    formData.append("meta_keywords", updatedData.keywords || "");
    formData.append("url_title", updatedData.url_title || "");
    formData.append("slug", updatedData.url_title || "");
    formData.append("canonical_url", updatedData.canonical_url || "");
    formData.append("focus_keyword", updatedData.focus_keyword || "");
    formData.append("og_title", updatedData.og_title || updatedData.title_tag || updatedData.title || "");
    formData.append("og_description", updatedData.og_description || updatedData.meta_description || "");
    formData.append("twitter_title", updatedData.og_title || updatedData.title_tag || updatedData.title || "");
    formData.append("twitter_description", updatedData.og_description || updatedData.meta_description || "");
    formData.append("meta_robots", updatedData.meta_robots || "index, follow");

    // Log FormData contents
    console.log("FormData contents:");
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value instanceof File ? value.name : value);
    }

    console.log("Sending update request for blog ID:", blogId, "with token:", token.substring(0, 20) + "...");

    const res = await fetch(`${API_URL}/api/update_blog/${blogId}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    const text = await res.text();
    let result;
    try {
      result = JSON.parse(text);
      console.log("Update Blog Response:", JSON.stringify(result, null, 2));
    } catch (parseErr) {
      console.error("Non-JSON response from server:");
      toast.error("Server returned invalid response.");
      return;
    }

    if (res.ok && (result.success || result.status)) {
      toast.success(result.message || "Blog updated successfully!");
      setFetchBlogs((prev) =>
        prev.map((b) => (b.id === blogId ? { ...b, ...updatedData } : b))
      );
      await getBlogs();
    } else {
      if (res.status === 401) {
        console.error("401 Unauthorized - Token:", token.substring(0, 20) + "...", "localStorage.apiToken:", localStorage.getItem("apiToken"));
        toast.error("Session expired. Please log in again.");
        logout();
      } else if (res.status === 422) {
        const errors = result.errors
          ? Object.values(result.errors).flat().join(", ")
          : result.message || "Validation error";
        console.error("Validation errors:", errors);
        toast.error(`Failed to update blog: ${errors}`);
      } else {
        console.error("API error response:", result);
        toast.error(result.message || `Error ${res.status}: Failed to update blog`);
      }
    }
  } catch (err) {
    console.error("Update blog error:", {
      message: err.message,
      stack: err.stack,
    });
    toast.error(`Failed to update blog: ${err.message}`);
  }
};

// =======================================================================================================// update Blog


  const deleteBlog = async (blogId) => {
    if (!isAuthenticated || !token) {
      console.error("No token for delete. isAuthenticated:", isAuthenticated, "Token:", token);
      toast.error("Please log in to delete blogs.");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/delete_blog/${blogId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const result = await res.json();
      console.log("Delete Blog Response:", JSON.stringify(result, null, 2));

      if (res.ok && (result.success || result.status)) {
        toast.success(result.message || "Blog deleted successfully!");
        setFetchBlogs((prev) => prev.filter((b) => b.id !== blogId));
      } else {
        if (res.status === 401) {
          console.error("401 Unauthorized - Token:", token.substring(0, 20) + "...");
          toast.error("Session expired. Please log in again.");
          logout();
        } else {
          toast.error(result.message || "Failed to delete blog");
        }
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete blog");
    }
  };

  useEffect(() => {
    if (isAuthenticated && token) {
      // console.log("Fetching blogs with token:", token.substring(0, 20) + "...");
      getBlogs();
    } else {
      setFetchBlogs([]);
      // console.log("No token, blogs cleared");
    }
  }, [token, isAuthenticated]);

  return (
    <BlogContext.Provider
      value={{
        fetchblogs,
        getBlogs,
        updateBlog,
        deleteBlog,
      }}
    >
      {children}
    </BlogContext.Provider>
  );
};

export const useBlog = () => useContext(BlogContext);